"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Mail,
  Phone,
  Edit,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import {
  fetchKYCStatusRequest,
  navigateToFirstRejectedStep,
} from "@/store/slices/kyc.slice";
import { getProblemDescription } from "@/types/models/kyc";
import KYCUpdateManager from "./KYCUpdateManager";

interface KycStatusStepProps {
  onContactSupport?: () => void;
  onReturnToDashboard?: () => void;
  storeId?: string;
}

export const KycStatusStep: React.FC<KycStatusStepProps> = ({
  onContactSupport,
  onReturnToDashboard,
  storeId,
}) => {
  const dispatch = useAppDispatch();
  const { kycStatus, rejectionReason, kycProblems, rejectedSteps } =
    useAppSelector((state) => state.kyc);
  const { userId, roles } = useAppSelector((state) => state.auth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showUpdateManager, setShowUpdateManager] = useState(false);

  // Get storeId from props or auth state
  const currentStoreId =
    storeId ||
    (roles && roles.length > 0 && roles[0].storeId
      ? roles[0].storeId
      : userId) ||
    "";

  // Determine whether we should treat the KYC as "verification in progress".
  // Show this state when all KYC steps are completed and verification is pending.
  const { completedSteps } = useAppSelector((state) => state.kyc);

  // Check if all required steps are completed
  const allStepsCompleted =
    completedSteps.businessDetails &&
    completedSteps.personalDetails &&
    completedSteps.documentUpload &&
    completedSteps.addressVerification &&
    completedSteps.videoVerification;

  const showVerificationInProgress =
    kycStatus === "PENDING_VERIFICATION" ||
    (kycStatus === "PENDING" && allStepsCompleted) ||
    (kycStatus === "IN_PROGRESS" && allStepsCompleted);

  // Set up polling for PENDING_VERIFICATION status (only when stage === 'completed')
  useEffect(() => {
    if (showVerificationInProgress && currentStoreId) {
      // Poll every 30 seconds
      intervalRef.current = setInterval(() => {
        dispatch(fetchKYCStatusRequest({ storeId: currentStoreId }));
      }, 30000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [showVerificationInProgress, currentStoreId, dispatch]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const getStatusConfig = () => {
    // If both status indicates pending verification and the backend stage is 'completed',
    // show the 'Verification in Progress' UI. Otherwise show the more generic pending state.
    if (kycStatus === "APPROVED") {
      return {
        icon: <CheckCircle className="w-16 h-16 text-green-500" />,
        title: "Verification Approved! 🎉",
        subtitle: "Your KYC verification has been successfully completed",
        description:
          "Congratulations! Your account is now fully verified and you can access all features of Shiphouse.",
        bgColor: "bg-green-500/10",
        borderColor: "border-green-500/20",
        textColor: "text-green-500",
        showActions: true,
      };
    }

    if (kycStatus === "REJECTED") {
      return {
        icon: <XCircle className="w-16 h-16 text-red-500" />,
        title: "Verification Rejected",
        subtitle: "Your KYC verification was not approved",
        description:
          rejectionReason ||
          "Please review the issues below and resubmit your documents.",
        bgColor: "bg-red-500/10",
        borderColor: "border-red-500/20",
        textColor: "text-red-500",
        showActions: true,
      };
    }

    if (showVerificationInProgress) {
      return {
        icon: <Clock className="w-16 h-16 text-blue-500" />,
        title: "Verification in Progress ✨",
        subtitle: "All details submitted successfully!",
        description:
          "Great job! You&apos;ve completed all KYC steps. Our team is now reviewing your information and will notify you once verification is complete. You don&apos;t need to worry about anything - just wait for our confirmation.",
        bgColor: "bg-blue-500/10",
        borderColor: "border-blue-500/20",
        textColor: "text-blue-500",
        showActions: false,
      };
    }

    return {
      icon: <AlertCircle className="w-16 h-16 text-blue-500" />,
      title: "Verification Pending",
      subtitle: "Please complete your KYC verification",
      description:
        "You need to complete all KYC steps to access your account features.",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      textColor: "text-blue-500",
      showActions: false,
    };
  };

  const statusConfig = getStatusConfig();

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default action - open email client
      window.location.href =
        "mailto:support@shiphouse.in?subject=KYC Support Request";
    }
  };

  const handleResubmitDocuments = () => {
    // Navigate to the first rejected step for resubmission
    dispatch(navigateToFirstRejectedStep());
  };

  const handleReturnToDashboard = () => {
    if (onReturnToDashboard) {
      onReturnToDashboard();
    } else {
      // Default action - navigate to dashboard with proper route structure
      const storeType =
        roles && roles.length > 0 ? roles[0].storeType : "seller"; // fallback to 'seller'
      const dashboardUrl = `/${storeType}/${currentStoreId}/dashboard`;
      window.location.href = dashboardUrl;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="text-center">
        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          <div
            className={`p-4 rounded-full ${statusConfig.bgColor} ${statusConfig.borderColor} border`}
          >
            {statusConfig.icon}
          </div>
        </div>

        {/* Status Title */}
        <h1 className="text-3xl font-bold text-white mb-2">
          {statusConfig.title}
        </h1>

        {/* Status Subtitle */}
        <p className={`text-lg ${statusConfig.textColor} mb-4`}>
          {statusConfig.subtitle}
        </p>

        {/* Status Description */}
        <p className="text-auth-text-muted text-base max-w-2xl mx-auto mb-8">
          {statusConfig.description}
        </p>

        {/* Status-specific Content */}
        {showVerificationInProgress && (
          <div className="bg-auth-card border border-auth-border rounded-2xl p-6 mb-8">
            <h3 className="text-white font-medium mb-4">
              🎉 You&apos;re all set! What happens next?
            </h3>
            <div className="space-y-3 text-left">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-auth-text-muted text-sm">
                  <span className="text-green-400 font-medium">Complete!</span>{" "}
                  All your documents and information have been submitted
                  successfully
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-medium">1</span>
                </div>
                <span className="text-auth-text-muted text-sm">
                  Our verification team is now reviewing your information
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-medium">2</span>
                </div>
                <span className="text-auth-text-muted text-sm">
                  Verification typically completes within 1-3 business days
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-medium">3</span>
                </div>
                <span className="text-auth-text-muted text-sm">
                  You&apos;ll receive an email notification once verification is
                  approved
                </span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm font-medium">
                💡 Pro tip: No action needed from you - just relax and wait for
                our confirmation!
              </p>
            </div>
          </div>
        )}

        {kycStatus === "REJECTED" && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 mb-8">
            <h3 className="text-red-500 font-medium mb-4">
              Verification Issues Found
            </h3>

            {/* General rejection reason */}
            {rejectionReason && (
              <div className="mb-4">
                <p className="text-auth-text-muted text-sm">
                  {rejectionReason}
                </p>
              </div>
            )}

            {/* Detailed problems list */}
            {kycProblems && kycProblems.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-red-400 font-medium text-sm">
                  Specific Issues:
                </h4>
                <div className="space-y-2">
                  {kycProblems.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-auth-text-muted text-sm">
                        {getProblemDescription(problem)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step-specific guidance - only show if there are rejected steps */}
            {rejectedSteps &&
              (rejectedSteps.businessDetails ||
                rejectedSteps.personalDetails ||
                rejectedSteps.documentUpload ||
                rejectedSteps.addressVerification ||
                rejectedSteps.videoVerification) && (
                <div className="mt-4 pt-4 border-t border-red-500/20">
                  <h4 className="text-red-400 font-medium text-sm mb-2">
                    Steps to Fix:
                  </h4>
                  <div className="space-y-1 text-xs text-auth-text-muted">
                    {rejectedSteps.businessDetails && (
                      <p>• Review and update your business details</p>
                    )}
                    {rejectedSteps.personalDetails && (
                      <p>• Verify and resubmit your personal information</p>
                    )}
                    {rejectedSteps.documentUpload && (
                      <p>• Upload clear, valid identity documents</p>
                    )}
                    {rejectedSteps.addressVerification && (
                      <p>• Provide valid address proof documents</p>
                    )}
                    {rejectedSteps.videoVerification && (
                      <p>
                        • Record a new video verification with better quality
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {kycStatus === "APPROVED" && (
            <button
              onClick={handleReturnToDashboard}
              className="px-8 py-3 bg-brand text-primary-foreground font-medium hover:bg-brand-hover transition-colors shadow-theme-brand"
            >
              Continue to Dashboard
            </button>
          )}

          {kycStatus === "REJECTED" && (
            <>
              <button
                onClick={() => setShowUpdateManager(true)}
                className="px-8 py-3 bg-brand text-primary-foreground font-medium hover:bg-brand-hover transition-colors shadow-theme-brand inline-flex items-center"
              >
                <Edit className="w-4 h-4 mr-2" />
                Update Specific Fields
              </button>
              <button
                onClick={handleResubmitDocuments}
                className="px-8 py-3 bg-auth-card border border-auth-border rounded-[30px] text-auth-text-muted hover:text-primary-foreground hover:border-brand transition-colors"
              >
                Resubmit All Steps
              </button>
              <button
                onClick={handleContactSupport}
                className="px-8 py-3 bg-auth-card border border-auth-border rounded-[30px] text-auth-text-muted hover:text-primary-foreground hover:border-brand transition-colors"
              >
                Contact Support
              </button>
            </>
          )}

          {showVerificationInProgress && (
            <button
              onClick={handleContactSupport}
              className="px-8 py-3 bg-auth-card border border-auth-border rounded-[30px] text-auth-text-muted hover:text-primary-foreground hover:border-brand transition-colors"
            >
              Contact Support
            </button>
          )}
        </div>

        {/* Support Information */}
        <div className="mt-12 pt-8 border-t border-auth-border">
          <h3 className="text-white font-medium mb-4">Need Help?</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@shiphouse.in"
              className="flex items-center space-x-2 text-auth-text-muted hover:text-brand transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>support@shiphouse.in</span>
            </a>
            <a
              href="tel:+911234567890"
              className="flex items-center space-x-2 text-auth-text-muted hover:text-brand transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>+91 123 456 7890</span>
            </a>
          </div>
        </div>
      </div>

      {/* KYC Update Manager Modal */}
      {showUpdateManager &&
        kycStatus === "REJECTED" &&
        kycProblems &&
        kycProblems.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Update KYC Fields
                  </h2>
                  <button
                    onClick={() => setShowUpdateManager(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <KYCUpdateManager
                  storeId={currentStoreId}
                  problems={kycProblems}
                  onUpdateComplete={() => {
                    setShowUpdateManager(false);
                    // Refresh KYC status after update
                    dispatch(
                      fetchKYCStatusRequest({ storeId: currentStoreId }),
                    );
                  }}
                />
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default KycStatusStep;
